/*
 * Catalog
 *
 * The products a dealership sells. Static rather than fetched: the content is
 * long-form marketing copy that belongs in version control next to the views
 * that render it, not in a data-entry screen.
 *
 * THERE ARE NO PRICES IN THIS FILE, and none should be added. A replicated
 * website never quotes — the dealership does, after seeing the home. Because
 * the field does not exist anywhere in the model, there is no path by which a
 * price can accidentally reach the page.
 *
 * Two catalogues, selected by getMode():
 *
 *   full   smoke, heat, carbon monoxide, water, Bed Shaker and
 *          the Comm Link — everything an authorized dealer installs
 *   water  the three products the online store sells, for dealerships on the
 *          flood-and-freeze brand
 *
 * Specifications are taken from the feature lists already used on the network
 * page (managers/Home.js) and from the owner's manuals the storefront cites
 * (CFWS10 / CFCL10 / CFBS520). Do not add a claim that is not sourced from one
 * of those — this copy appears on a real company's website under their name.
 */

// Dealerships on the water brand. Used only as a fallback when the dealership
// record does not say — see getMode().
const WATER_BRAND_DOMAINS = ['floodandfreezenetwork.com'];

const Catalog = {

    products: {

        commLink: {
            category: 'The hub',
            description: 'The Comm Link is the brain of the network. It connects every sensor in the home and relays their status to your phone in real time, over Ethernet or Wi-Fi, on AC power with no batteries to replace.',
            features: [
                'Connects every device on the network',
                'Relays alerts to your phone, by text, email and push',
                'Ethernet or Wi-Fi',
                'AC powered — no batteries to replace'
            ],
            handle: 'comm-link',
            icon: 'broadcast-pin',
            id: 'comm-link',
            images: [
                { altText: 'Comm Link, front', src: 'images/comm-link-front-white.jpg' },
                { altText: 'Comm Link, back', src: 'images/comm-link-back-white.jpg' }
            ],
            shortTitle: 'Comm Link',
            subtitle: 'Central command center',
            title: 'Comm Link',
            water: true
        },

        bedShaker: {
            category: 'Bedrooms',
            description: 'Most fatal home fires happen at night. The Bed Shaker pairs the most effective waking sound with a shaker that vibrates the mattress, so everyone wakes and knows which room the danger is in.',
            features: [
                '520 Hz low-frequency horn — the most effective waking sound',
                'Under-mattress shaker unit',
                'Tells you which room the danger is in',
                'More effective for children, adults over 40 and the hearing impaired'
            ],
            handle: 'bed-shaker',
            icon: 'moon-stars-fill',
            id: 'bed-shaker',
            images: [
                { altText: 'Bed Shaker', src: 'images/bedside-control-center-front-white.jpg' },
                { altText: 'Bed Shaker in a bedroom', src: 'images/omnishield-bed-shaker.jpg' }
            ],
            shortTitle: 'Bed Shaker',
            subtitle: 'Wakes you, and tells you where',
            title: 'Bed Shaker',
            water: true
        },

        smokeSensor: {
            category: 'Fire',
            description: 'Many smoke detectors in homes today are unreliable — dead or missing batteries, poor placement, dust build-up, or simple age. Ours uses two technologies to catch both slow smouldering and fast flaming fires, with fewer false alarms, in a chamber that can be removed and cleaned.',
            features: [
                'Two detection technologies — smouldering and flaming fires',
                '360° unobstructed smoke chamber, removable and cleanable',
                'Infrared optics scan the chamber every 4 seconds',
                'Sealed 10-year industrial battery'
            ],
            handle: 'smoke-sensor',
            icon: 'fire',
            id: 'smoke-sensor',
            images: [
                { altText: 'Smoke Sensor', src: 'images/omnishield-smoke-sensor.jpg' }
            ],
            shortTitle: 'Smoke Sensor',
            subtitle: 'Two technologies, fewer false alarms',
            title: 'Smoke Sensor'
        },

        heatSensor: {
            category: 'Fire',
            description: 'Attics, laundry rooms and garages are poor places for a smoke alarm — the activity in those rooms sets it off. That leaves gaps in a home’s fire safety. The Heat Sensor closes them, watching for both a fixed temperature and a rapid rise.',
            features: [
                'Rate-of-rise detection — catches a fast climb before any threshold',
                'Three fixed-temperature settings — 120°, 135°, 174°',
                'Built for attics, garages and laundry rooms',
                'Sealed 10-year industrial battery'
            ],
            handle: 'heat-sensor',
            icon: 'thermometer-high',
            id: 'heat-sensor',
            images: [
                { altText: 'Heat Sensor', src: 'images/omnishield-heat-sensor.jpg' }
            ],
            shortTitle: 'Heat Sensor',
            subtitle: 'Rooms a smoke alarm cannot watch',
            title: 'Heat Sensor'
        },

        coSensor: {
            category: 'Carbon monoxide',
            description: 'Carbon monoxide is odorless, colorless and undetectable by people. No home should be without monitoring for it. The network detects the silent killer and alerts you in several ways before levels become lethal.',
            features: [
                'Electro-chemical CO sensor',
                'Texas Instruments microprocessor controlled',
                'Alerts on device, by horn and light, and on your phone',
                'Sealed 10-year industrial battery'
            ],
            handle: 'co-sensor',
            icon: 'cloud-haze2-fill',
            id: 'co-sensor',
            images: [
                { altText: 'Carbon Monoxide Sensor', src: 'images/omnishield-co-sensor.jpg' }
            ],
            shortTitle: 'CO Sensor',
            subtitle: 'For the one you cannot smell',
            title: 'Carbon Monoxide Sensor'
        },

        waterSensor: {
            category: 'Water',
            description: '37% of homes have claimed water damage on their insurance. The average washing machine hose leaks every 8.7 years and three quarters of water heaters fail in their twelfth year. The Water Sensor catches the first drop — and the freeze that comes before a burst pipe.',
            features: [
                '23-karat gold contacts sense the first drop',
                'Low-temperature alert warns before a pipe bursts',
                'Repeats the network signal to extend coverage',
                '10-year lithium battery'
            ],
            handle: 'water-sensor',
            icon: 'droplet-fill',
            id: 'water-sensor',
            images: [
                { altText: 'Water Sensor, front', src: 'images/water-sensor-front-white.jpg' },
                { altText: 'Water Sensor, back', src: 'images/water-sensor-back-white.jpg' }
            ],
            shortTitle: 'Water Sensor',
            subtitle: 'Leak and freeze detection',
            title: 'Water Sensor',
            water: true
        }
    },

    /*
     * Which catalogue this dealership sells.
     *
     * Nothing in replicated_website_preferences distinguishes a water-only
     * dealership today, so the domain is the fallback: floodandfreezenetwork
     * sits alongside homesafealerts and ournetworkprotects in index.php as a
     * separate brand. A `water_only` flag on the dealership record is the
     * durable answer and takes precedence the moment it exists.
     */
    getMode: dealership => {

        if(typeof(dealership?.water_only) === 'boolean') {
            return dealership.water_only === true ? 'water' : 'full';
        }

        let domain = String(window.location.hostname || '').replace('www.', '').toLowerCase();
        return WATER_BRAND_DOMAINS.includes(domain) === true ? 'water' : 'full';
    },

    // Ordered for the page: the hub first, since nothing works without it,
    // then the sensors by what they watch for, then the bedroom device.
    getAll: mode => {
        let { bedShaker, coSensor, commLink, heatSensor, smokeSensor, waterSensor } = Catalog.products;
        let ordered = [smokeSensor, heatSensor, coSensor, waterSensor, bedShaker, commLink];
        return mode === 'water' ? ordered.filter(product => product.water === true) : ordered;
    },

    getByHandle: (handle, mode) => {
        return Catalog.getAll(mode).find(product => product.handle === handle) || null;
    },

    /*
     * Whether this dealership shows the products themselves: the product
     * pages, the quote list and its request form, the coverage quiz that builds
     * one, and any photograph or video of the devices. The products can still
     * be talked about in words everywhere else.
     *
     * Only an explicit false hides them, so a dealership whose preferences
     * predate `show_omnishield_products` keeps the site it already had.
     */
    isVisible: dealership => {
        return dealership?.show_omnishield_products !== false;
    }
}

export default Catalog;
